'use client'

/**
 * WHERE THE BAR GOES IN THE MEETING ROOM (#15834).
 *
 * The room's footer — private notes, the highlight input, the create bar — is
 * an in-flow block at the bottom of an `h-dvh` frame, exactly where the bar
 * floats. So in the room the bar does not float: the room's header Record
 * control publishes an anchor element here, and the host renders the bar into
 * it as a popover under that control. Every state the bar has — the consent
 * prompt, the microphone check and its errors, a lost microphone, Pause and
 * Stop, an interrupted capture, Saved and notes ready — stays reachable, and
 * none of it covers the footer.
 *
 * A publish/subscribe store rather than a context for the reason
 * `meeting-page-context.ts` gives: the host is mounted in the root layout,
 * above the router, and the room's header is rendered by a route.
 *
 * The anchor IS the signal that the header control is live. Until it mounts
 * (and wherever it never does — a DONE meeting has no Record control) the
 * room keeps its old rule, and the footer's clearance padding keeps a
 * floating bar off its content.
 *
 * ## Arriving with a capture already running (#16939)
 *
 * The header's chip already says "Recording 02:26". A popover opened under it
 * on arrival said the same thing again and covered the section header. So an
 * anchor published while a capture is running starts FOLDED: the chip is the
 * collapsed bar, and the popover opens only when the chip asks for it — its
 * disclosure toggle ({@link setRecordingBarRoomPopoverOpen}), or the chip's
 * live view, which lives in the unfolded bar. A capture started in the room
 * itself keeps its popover, and anything else the bar has to say (an error,
 * a lost microphone, Saved) is shown folded or not — see the host.
 *
 * While the bar is HELD open — the live view's panel is open in it, or it
 * has something else to say — the fold is not the toggle's to change, and
 * the host publishes that ({@link useRecordingBarRoomHeldOpen}) so the
 * toggle steps aside rather than read "Hide" over a bar it cannot hide.
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useSyncExternalStore,
} from 'react'
import { createPortal } from 'react-dom'

import { getRecordingSessionSnapshot } from '@/lib/notetaker/recording-session-store'

import { useLiveDraftPanelOpen } from './live-draft-panel-store'

let anchor: HTMLElement | null = null
/** Whether the room's popover is unfolded. See the module header. */
let popoverOpen = true
/** Whether the bar is shown whatever the fold says. See the module header. */
let heldOpen = false
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Publish `element` as the room's anchor. */
export function publishRecordingBarRoomAnchor(element: HTMLElement): void {
  if (anchor === element) return
  anchor = element
  // Arriving with a capture running folds it into the chip (#16939).
  popoverOpen = getRecordingSessionSnapshot().status === 'idle'
  notify()
}

/** Fold or unfold the room's popover — the header chip's toggle. */
export function setRecordingBarRoomPopoverOpen(open: boolean): void {
  if (popoverOpen === open) return
  popoverOpen = open
  notify()
}

/** Whether the room's popover is unfolded. */
export function useRecordingBarRoomPopoverOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => popoverOpen,
    () => true,
  )
}

/**
 * Whether the bar is shown in the room whatever the fold says: the toggle's
 * cue to step aside.
 */
export function useRecordingBarRoomHeldOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => heldOpen,
    () => false,
  )
}

function setRecordingBarRoomHeldOpen(held: boolean): void {
  if (heldOpen === held) return
  heldOpen = held
  notify()
}

/**
 * Whether the host should keep a running capture folded into the room's
 * header chip: there is an anchor, a capture is live, and nothing has asked
 * for the popover. Opening the chip's live view unfolds it (the panel lives
 * in the unfolded bar), and a capture that ends unfolds it for the next one.
 * While the panel is open or the bar has something else to say, it is held
 * open, and that is published for the toggle.
 *
 * @param liveRecordingId - The running capture's recording, or `null` when
 *   nothing is captured.
 * @param alerting - The bar has something to say besides the capture (an
 *   error, a lost microphone, Saved), so it shows folded or not.
 */
export function useRecordingBarRoomFolded(
  liveRecordingId: string | null,
  alerting = false,
): boolean {
  const current = useRecordingBarRoomAnchor()
  const open = useRecordingBarRoomPopoverOpen()
  const panelOpen = useLiveDraftPanelOpen(liveRecordingId)
  const live = liveRecordingId !== null
  const held = current !== null && live && (panelOpen || alerting)
  useEffect(() => {
    if (panelOpen || !live) setRecordingBarRoomPopoverOpen(true)
  }, [panelOpen, live])
  useEffect(() => {
    setRecordingBarRoomHeldOpen(held)
  }, [held])
  useEffect(() => () => setRecordingBarRoomHeldOpen(false), [])
  return current !== null && live && !open && !held
}

/**
 * Withdraw `element`, and only `element`: a newer anchor published in the
 * meantime (a soft navigation between two rooms) is left in place.
 */
export function clearRecordingBarRoomAnchor(element: HTMLElement): void {
  if (anchor !== element) return
  anchor = null
  notify()
}

/** Test-only: forget whatever was published. */
export function resetRecordingBarRoomAnchor(): void {
  anchor = null
  popoverOpen = true
  heldOpen = false
  notify()
}

/** The anchor the bar should render into, or `null` to float as usual. */
export function useRecordingBarRoomAnchor(): HTMLElement | null {
  return useSyncExternalStore(
    subscribe,
    () => anchor,
    () => null,
  )
}

/** A callback ref that publishes its element while it is mounted. */
export function useRecordingBarRoomAnchorRef(): (
  element: HTMLElement | null,
) => (() => void) | undefined {
  return useCallback((element: HTMLElement | null) => {
    if (!element) return undefined
    publishRecordingBarRoomAnchor(element)
    return () => clearRecordingBarRoomAnchor(element)
  }, [])
}

/**
 * Render `bar` into the room's anchor when there is one, else where it
 * stands — the one line the host spends on placement.
 */
export function inRecordingBarRoomAnchor(
  anchorElement: HTMLElement | null,
  bar: ReactNode,
): ReactNode {
  return anchorElement && bar ? createPortal(bar, anchorElement) : bar
}
